import { DefaultHttpClient, HttpClient, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { isSessionId } from './events';

export function createTokenProvider(instance, account, scope) {
  return async () => {
    if (!instance || !account) throw Object.assign(new Error('REALTIME_SIGN_IN_REQUIRED'), { status: 401 });
    try {
      const result = await instance.acquireTokenSilent({ account, scopes: [scope] });
      if (!result.accessToken) throw new Error('EMPTY_TOKEN');
      return result.accessToken;
    } catch {
      // Never launch a redirect or popup from background refresh work.
      throw Object.assign(new Error('REALTIME_SIGN_IN_REQUIRED'), { status: 401 });
    }
  };
}

export function createGatewayClient(config, getToken, fetchImpl = (...args) => fetch(...args)) {
  async function request(method, body, signal) {
    if (!config.enabled) return undefined;
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) controller.abort();
    signal?.addEventListener('abort', abort);
    const timeout = setTimeout(abort, 15000);
    let rejectOnAbort;
    const aborted = new Promise((resolve, reject) => {
      rejectOnAbort = () => reject(new Error('REALTIME_REQUEST_ABORTED'));
      controller.signal.addEventListener('abort', rejectOnAbort);
      if (controller.signal.aborted) rejectOnAbort();
    });
    try {
      const token = await Promise.race([Promise.resolve().then(getToken), aborted]);
      if (controller.signal.aborted) throw new Error('REALTIME_REQUEST_ABORTED');
      const response = await Promise.race([fetchImpl(`${config.gatewayUrl}/subscriptions`, {
        method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body), signal: controller.signal, credentials: 'omit', redirect: 'error', cache: 'no-store',
      }), aborted]);
      if (!response.ok) throw Object.assign(new Error('REALTIME_GATEWAY_REQUEST_FAILED'), { status: response.status });
      return response.status === 204 ? undefined : await Promise.race([response.json(), aborted]);
    } finally {
      clearTimeout(timeout); signal?.removeEventListener('abort', abort);
      controller.signal.removeEventListener('abort', rejectOnAbort);
    }
  }
  return { subscribe: (body, signal) => request('POST', body, signal),
    removeSession: sessionId => request('DELETE', { sessionId }) };
}

export class SessionHttpClient extends HttpClient {
  constructor(config, delegate, onSession, onHttpError = () => {}) {
    super(); this.config = config; this.delegate = delegate; this.onSession = onSession; this.onHttpError = onHttpError;
  }
  async send(request) {
    const url = new URL(request.url);
    if (url.username || url.password || url.hash) throw new Error('UNEXPECTED_REALTIME_ORIGIN');
    const gateway = new URL(`${this.config.gatewayUrl}/negotiate`);
    const isGateway = url.origin === gateway.origin && url.pathname === gateway.pathname;
    if (!isGateway && (url.origin !== this.config.signalrOrigin || !url.pathname.startsWith('/client/'))) throw new Error('UNEXPECTED_REALTIME_ORIGIN');
    const response = await this.delegate.send(request);
    if (isGateway) {
      if (response.statusCode !== 200) { this.onHttpError(response.statusCode); return response; }
      const body = JSON.parse(response.content);
      const redirect = new URL(body.url);
      if (!isSessionId(body.realtimeSessionId) || body.subscriptionTtlSeconds !== 900
        || redirect.origin !== this.config.signalrOrigin || redirect.pathname !== '/client/'
        || redirect.username || redirect.password || redirect.hash || redirect.searchParams.get('hub') !== 'portalChanges'
        || typeof body.accessToken !== 'string' || !body.accessToken) throw new Error('INVALID_REALTIME_NEGOTIATION');
      this.onSession(body.realtimeSessionId);
    } else if (response.statusCode === 200 && url.pathname.endsWith('/negotiate')) {
      // Prevent an additional service redirect from taking a token to another origin.
      const body = JSON.parse(response.content);
      if (body.url) throw new Error('UNEXPECTED_SIGNALR_REDIRECT');
    }
    return response;
  }
  getCookieString(url) { return this.delegate.getCookieString(url); }
}

export function createGatewayConnection({ config, getToken, onSession, onHttpError }) {
  if (!config.enabled) return null;
  const client = new SessionHttpClient(config, new DefaultHttpClient({ log: () => {} }), onSession, onHttpError);
  // Runtime restarts from the gateway after close to obtain a fresh authenticated session.
  // Preserve the SDK's normal negotiation and transport fallback; do not force WebSockets.
  return new HubConnectionBuilder().withUrl(config.gatewayUrl, {
    accessTokenFactory: getToken, httpClient: client, withCredentials: false, timeout: 15000,
  }).configureLogging(LogLevel.None).build();
}
